import filter from 'lodash/filter';
import get from 'lodash/get';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import uniqueId from 'lodash/uniqueId';
import sanitizeHtml from 'sanitize-html';
import { COLUMN } from '@yme/atoms/grid/constants';
import { CookiesConsentKeys } from '@yme/components/Cookies/constants';
import { LIMIT_PER_PAGE } from '@yme/organisms/news-events-grid/constants';
import { ACCESSORY_HIDE_PRODUCT_COMPATIBILITY, PRODUCT_MOTORCYCLE_POWER_VERSION, PRODUCT_MOTORCYCLE_TRANSMISSION, SHOW_NEW_LABEL, SHOW_NEW_LABEL_UNTIL, } from '@yme/selectors/constants';
import { colorBrandYamahaBlue, colorBrandYamahaMetal, colorBrandYamahaRed, colorCoreBlack900Base, colorGradientYMEBlue, colorGradientYMEGrey, colorGradientYMERed, } from '@yme/tokens/colors';
import { BREAKPOINT } from '@yme/tokens/screen';
import { getMasterVariant } from '@yme/utils/product';
import { ADDITIONAL_COUNTRIES_MAP, ProductType, UK_PREFIX } from '../constants';
import { BLANK, DEFAULT_LANG, DEGREES_360, DESKTOP, DESKTOP_KEY, ENABLE_JS_API, EUROPEAN_UNION, IMG_POSTFIX, MILES_IN_KM, MOBILE, MOBILE_KEY, OF_AMOUNT_TEMPLATE, POWER_VERSION_35KW, SHOWING_AMOUNT_TEMPLATE, STUDIO_IMAGE, TABLET_KEY, THUMBNAIL_KEY, YAMT, YOUTUBE_LINK_START, YOUTUBE_PLAYLIST_LINK_START, YOUTUBE_PREVIEW_IMAGE_NAME, YOUTUBE_PREVIEW_IMAGE_URL, YOUTUBE_THUMBNAIL_IMAGE_NAME, } from './constants';
import { regExpAnyCharacters, regExpMobileDevices, regExpSpaces } from './regEx';
import sanitizeWhiteList from './sanitize-html.whitelist';
export const escapeImageUrl = (imageUrl) => {
    // @todo - revisit this approach in scope of B2C-21738
    //  imageUrl.replace(reqExpHasBrackets, reqExpEscapedBrackets);
    // for now hotfixing without replacing brackets
    return imageUrl;
};
export { setDigitalData, DigitalDataMutation, isInsideClickEventFromProductOptions, isInsideGenericClickEvent, adobeLaunch, digitalData, categoryNameFromKey, normalizeString, mapToAdobeDataLayerProduct, } from '../../utils/digital-data';
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = () => { };
export function getClearedValue(inputName) {
    return inputName
        .toString()
        .trim()
        .replace(regExpAnyCharacters, ' ')
        .replace(regExpSpaces, '_')
        .toLowerCase();
}
export function getLowerCaseValue(inputName) {
    return inputName.toString().trim().replace(' ', '').toLowerCase();
}
// Util for formatting language root to 'nl_nl' format
export function getFormattedLanguageRoot(rootLanguage) {
    return rootLanguage.replace('-', '_').toLowerCase();
}
export function getFormattedLanguageRootForProductRelease(rootLanguage) {
    return rootLanguage.replace('-', '_');
}
export function reloadPage() {
    setTimeout(() => {
        window.location.reload();
    });
}
export function getWebsiteAddress(opt) {
    let address = '';
    if (Array.isArray(opt)) {
        address = (opt.find((item) => item && item.trim().length) || '').trim();
    }
    else {
        address = (opt || '').trim();
    }
    if (address.length === 0) {
        return undefined;
    }
    return setProtocolURL(address);
}
// Special util for build uniq key or any uniqValue
export function buildKey(arg1, arg2) {
    const clearedArg1 = arg1 ? getClearedValue(arg1) : 'id';
    if (arg2 === 0 || arg2) {
        return clearedArg1.length ? `${clearedArg1}_${getClearedValue(arg2)}` : getClearedValue(arg2);
    }
    return uniqueId(clearedArg1);
}
// Utils for detect device from 'navigator.userAgent'
function deviceDetect() {
    return regExpMobileDevices.test(navigator.userAgent) ? MOBILE : DESKTOP;
}
// simple util for check current device - "is mobile?" or not
export function hasMobileDevice() {
    return deviceDetect() === MOBILE;
}
export const selectColorTheme = (colorKey = 'feel') => {
    const colorKeyToLowerCase = colorKey?.toLowerCase();
    const colorVariants = {
        none: colorCoreBlack900Base,
        feel: colorBrandYamahaRed,
        race: colorBrandYamahaBlue,
        move: colorBrandYamahaMetal,
    };
    return colorVariants[colorKeyToLowerCase] || colorBrandYamahaRed;
};
export function getGradientColor(colorVariants) {
    switch (colorVariants) {
        case colorBrandYamahaRed:
            return colorGradientYMERed;
        case colorBrandYamahaBlue:
            return colorGradientYMEBlue;
        case colorBrandYamahaMetal:
            return colorGradientYMEGrey;
        default:
            return '';
    }
}
function hasSafariBrowser() {
    return navigator.userAgent.toLowerCase().indexOf('safari/') > -1;
}
/**
 * @param url for passing
 * @param option possible options: SELF = '_self'; BLANK = '_blank'; PARENT = '_parent'; TOP = '_top';
 *
 * @return window open event
 */
export function onOpenWindow(url, option = BLANK) {
    const isSafariBrowser = hasSafariBrowser();
    if (isSafariBrowser) {
        return window.open(url, option);
    }
    return window.open(url, option, 'noopener');
}
export function getFormattedDate({ date, lang = 'en-US', }) {
    if (!date) {
        return '';
    }
    if (!lang) {
        lang = 'en-US';
    }
    return new Intl.DateTimeFormat(lang, {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
    }).format(new Date(date));
}
export function getImageSrc(src = '', imageSuffix) {
    const imageSrc = src.split('.');
    imageSrc[imageSrc.length - 2] = `${imageSrc.at(-2)}${imageSuffix}`;
    return imageSrc.join('.');
}
export const getQueryParamByName = (search, name) => {
    const urlSearchParams = new URLSearchParams(search);
    return Object.fromEntries(urlSearchParams.entries())[name];
};
export const deleteQueryParamByName = (param, history) => {
    const queryParams = new URLSearchParams(history.location.search);
    queryParams.delete(param);
    history.replace({
        search: queryParams.toString(),
    });
};
export const getToForLinkComponent = (link) => {
    if (isExternalLink(link)) {
        return {
            pathname: link,
        };
    }
    else {
        return link;
    }
};
export const buildYoutubeUrl = (youTubeId, isPlayList = false) => {
    if (isPlayList) {
        return `${YOUTUBE_PLAYLIST_LINK_START}${youTubeId}&${ENABLE_JS_API}=1`;
    }
    return `${YOUTUBE_LINK_START}/${youTubeId}?${ENABLE_JS_API}=1`;
};
export const getYoutubePreview = (youtubeId, isThumbnail) => {
    const imageName = isThumbnail ? YOUTUBE_THUMBNAIL_IMAGE_NAME : YOUTUBE_PREVIEW_IMAGE_NAME;
    return `${YOUTUBE_PREVIEW_IMAGE_URL}/${youtubeId}/${imageName}`;
};
// Generate random string/characters
export function buildStateParam() {
    return (Math.random() + 1).toString(36).substring(7);
}
// Returns sanitized HTML
export function getSanitizeHtml(inputText) {
    const clearedText = inputText?.replace(/'&nbsp;'/g, ' ');
    return sanitizeHtml(clearedText, sanitizeWhiteList);
}
function checkImageByName(image, name) {
    return image.url.toLowerCase().includes(name);
}
export function toStringArray(value) {
    return value ? (Array.isArray(value) ? value : [value]) : [];
}
export function getItemImage(images) {
    return (images.find((image) => checkImageByName(image, STUDIO_IMAGE)) ??
        images.find((image) => checkImageByName(image, DEGREES_360)) ??
        images[0]);
}
export function getAttributeByName(attributes, name) {
    return attributes.find((attribute) => attribute.name === name)?.value ?? '';
}
export function formatDistance(distance, t) {
    const isUK = window.location.pathname.startsWith(UK_PREFIX);
    const value = isUK ? Number((distance * MILES_IN_KM).toFixed(2)) : distance;
    const translationToken = isUK ? 'common_mile_label' : 'common_km_label';
    return `${value}${t(translationToken)}`;
}
export function getAttributesByNames(attributes, names, labels) {
    return attributes.reduce((acc, { name, value }) => {
        const label = Object.keys(labels).find((item) => item === name);
        if (names.includes(name) && value && label) {
            acc[labels[label]] = value;
        }
        return acc;
    }, {});
}
/**
 * @param value from attributes object by key 'name' (string | Object)
 ** case exp1: value = {'en-GB': 'Standard rotation', 'nl-NL': 'Standard rotation', 'en': 'Standard rotation'};
 ** case exp2: value = {'en-GB': 'Standard rotation'};
 ** case exp3: value = 'Standard rotation';
 ** case exp4: value = '';
 * @param lang current language for App (by location)
 */
export function getValueOptionByLang(value, lang) {
    if (typeof value === 'string') {
        return value.trim();
    }
    const result = get(value, lang, '') || get(value, DEFAULT_LANG, '');
    return result.trim();
}
export function scrollToId({ id, yOffset = 0, k = 0, }) {
    if (isString(id) && id.trim().length === 0) {
        return window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }
    const element = isString(id) && document.getElementById(id);
    if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset - yOffset + k;
        return window.scrollTo({
            top: y,
            behavior: 'smooth',
        });
    }
    return undefined;
}
export function interceptExternalRedirect(redirectUrl) {
    if (redirectUrl) {
        if (isExternalLink(redirectUrl)) {
            // we need timeout here to complete the page load and then redirect
            setTimeout(() => {
                window.location.replace(redirectUrl);
            });
            return true;
        }
    }
    return false;
}
export function isExternalLink(link) {
    return !link.startsWith('/');
}
// Smartfit of language options, this list with all possible options described here:
// https://support.yamnet.com/browse/B2C-9607
// https://support.yamnet.com/confluence/pages/viewpage.action?pageId=95981949
// https://docs.onlinesizing.bike/docs/online-sizing/add-global-configuration/#language
export const languageFrameSizeAdapter = {
    'nl-nl': 'nl',
    'be-fr': 'fr',
    'be-nl': 'nl',
    'fr-fr': 'fr',
    'de-de': 'de',
    'it-it': 'it',
    'es-es': 'es',
    'gb-en': 'en',
    en: 'en',
};
export function getIsExternalLink(link) {
    return Boolean(link.toLowerCase().trim().startsWith('http'));
}
export function isPDFlink(link) {
    return link.toLowerCase().endsWith('.pdf');
}
export function getStreetWithAdditionalAddress(address, additionalAddress) {
    return `${address || ''}${additionalAddress || ''}`;
}
export function getOffsetForScrollTo({ isMobileView, headerStickyMenuShown, }) {
    if (!isUndefined(isMobileView) && !isUndefined(headerStickyMenuShown)) {
        const amountOfHeightMenu = isMobileView ? 64 : 80;
        return headerStickyMenuShown ? amountOfHeightMenu : amountOfHeightMenu * 2;
    }
    return undefined;
}
export function buildRandomValue() {
    return (Math.random() + 1).toString(36).substring(7);
}
export function buildDynamicSrc(src) {
    const t = new Date().getTime;
    return `${src}?t=${t}`;
}
export function getImageUrlByDimension({ src, dimension }) {
    if (!src) {
        return '';
    }
    const arr = src.split('.');
    let postfix = '';
    if (dimension === THUMBNAIL_KEY) {
        postfix = IMG_POSTFIX[THUMBNAIL_KEY];
    }
    if (dimension === MOBILE_KEY) {
        postfix = IMG_POSTFIX[MOBILE_KEY];
    }
    if (dimension === TABLET_KEY) {
        postfix = IMG_POSTFIX[TABLET_KEY];
    }
    if (dimension === DESKTOP_KEY) {
        postfix = IMG_POSTFIX[DESKTOP_KEY];
    }
    arr[arr.length - 2] = `${arr.at(-2)}${postfix}`;
    return arr.join('.');
}
const getZeroToSingleDataPeriod = (period) => period < 10 ? `0${period}` : `${period}`;
const getHoursTwoDigit = (date) => date.getHours().toString().padStart(2, '0');
export const getDateWithCapitalLetterMonth = (formatter, date) => {
    const month = formatter.formatToParts(date).find((part) => part.type === 'month')?.value;
    const day = formatter.formatToParts(date).find((part) => part.type === 'day')?.value;
    const monthCapitalized = `${month?.charAt(0)?.toUpperCase()}${month?.slice(1)}`;
    const dayFormatted = getZeroToSingleDataPeriod(Number(day));
    const dateFromFormatted = formatter.format(date);
    return dateFromFormatted.replace(day || '', dayFormatted).replace(month || '', monthCapitalized);
};
export const prepareDateRangeText = ({ dateFrom, dateTo, lang = 'en-US', hasTime = true, isShort = false, }) => {
    const dateFromObject = new Date(dateFrom);
    const dateToObject = new Date(dateTo);
    const isSameMonth = dateFromObject.getMonth() === dateToObject.getMonth();
    const isSameYear = dateFromObject.getFullYear() === dateToObject.getFullYear();
    const isSameDay = dateFromObject.getDate() === dateToObject.getDate();
    const timeSeparator = isShort ? ' |' : ',';
    const time = hasTime &&
        (dateFromObject.getHours() === dateToObject.getHours() &&
            dateFromObject.getMinutes() === dateToObject.getMinutes()
            ? `${getHoursTwoDigit(dateToObject)}:${getZeroToSingleDataPeriod(dateToObject.getMinutes())}`
            : `${getHoursTwoDigit(dateFromObject)}:${getZeroToSingleDataPeriod(dateFromObject.getMinutes())} – ${dateToObject.getHours()}:${getZeroToSingleDataPeriod(dateToObject.getMinutes())}`);
    const options = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    };
    const formatter = new Intl.DateTimeFormat(lang, options);
    const dateFromFormatted = getDateWithCapitalLetterMonth(formatter, dateFromObject);
    const dateToFormatted = getDateWithCapitalLetterMonth(formatter, dateToObject);
    const bothDatesFormatted = `${dateFromFormatted} – ${dateToFormatted}${time ? `${timeSeparator} ${time}` : ''}`;
    if (isSameDay && isSameMonth && isSameYear) {
        return `${dateToFormatted} ${time ? `${timeSeparator} ${time}` : ''}`;
    }
    if (isSameYear && isSameMonth) {
        return `${isShort
            ? dateFromFormatted.split(',')[0]
            : getZeroToSingleDataPeriod(dateFromObject.getDate())} – ${dateToFormatted}${time ? `${timeSeparator} ${time}` : ''}`;
    }
    return bothDatesFormatted;
};
export const replaceShowingText = ({ label, perPage = 0, total, currentPage, }) => {
    const startIndex = total > 0 ? (currentPage - 1) * perPage + 1 : 0;
    const endIndex = Math.min(currentPage * perPage, total);
    const amount = `${startIndex}-${endIndex}`;
    return label.replace(SHOWING_AMOUNT_TEMPLATE, amount).replace(OF_AMOUNT_TEMPLATE, `${total}`);
};
export const filterNotSupCountries = (countries, currentCountry) => {
    if (currentCountry === EUROPEAN_UNION)
        return countries;
    return filter(countries, (country) => country.code !== EUROPEAN_UNION);
};
export const getAdaptivePerPageLimit = (width) => {
    if (width >= BREAKPOINT.md && width < BREAKPOINT.lg) {
        return LIMIT_PER_PAGE.limitTablet;
    }
    return width > BREAKPOINT.lg ? LIMIT_PER_PAGE.limitDesktop : LIMIT_PER_PAGE.limitMobile;
};
export const getCardsColumnNumberForDesktop = (cardsLength, maxCards) => {
    if (cardsLength !== maxCards) {
        return COLUMN.lg / cardsLength;
    }
    return COLUMN.lg / maxCards;
};
export const getCardsColumnNumberForTablet = (cardsLength) => cardsLength !== 1 ? COLUMN.md / 2 : COLUMN.md;
const setProtocolURL = (url = '') => {
    if (url.startsWith('http')) {
        return url;
    }
    return `https://${url}`;
};
export const compareProductAttribute = (masterVariant, attribute, value) => {
    const productAttributeValue = masterVariant
        ? getAttributeByName(masterVariant?.attributes, attribute)?.en
        : '';
    return productAttributeValue === value;
};
export const checkVariationsYAMTTransmissionsExist = (products) => {
    return products.some((product) => compareProductAttribute(getMasterVariant(product?.variants), PRODUCT_MOTORCYCLE_TRANSMISSION, YAMT));
};
export const checkYAMTTransmissionExist = (product) => {
    const masterVariant = getMasterVariant(product?.variants);
    const isYAMTTransmission = compareProductAttribute(masterVariant, PRODUCT_MOTORCYCLE_TRANSMISSION, YAMT);
    const isSomeOfVariationsHasYAMTTransmission = checkVariationsYAMTTransmissionsExist(product?.groupingProducts || []);
    return isYAMTTransmission || isSomeOfVariationsHasYAMTTransmission;
};
export const checkVariations35KWPowerVersionExist = (products) => {
    return products.some((product) => compareProductAttribute(getMasterVariant(product?.variants), PRODUCT_MOTORCYCLE_POWER_VERSION, POWER_VERSION_35KW));
};
export const check35KWPowerVersionExist = (product) => {
    const masterVariant = getMasterVariant(product?.variants);
    const is35KWPowerVersion = compareProductAttribute(masterVariant, PRODUCT_MOTORCYCLE_POWER_VERSION, POWER_VERSION_35KW);
    const isSomeOfVariationsHas35KWPoverVersion = checkVariations35KWPowerVersionExist(product?.groupingProducts || []);
    return is35KWPowerVersion || isSomeOfVariationsHas35KWPoverVersion;
};
export const getAdditionalCounties = (isoCountryCode) => {
    if (!isoCountryCode) {
        return [];
    }
    return ADDITIONAL_COUNTRIES_MAP?.[isoCountryCode.toLowerCase()] || [];
};
export const getIsAllCookiesAccepted = (optanonConsent) => {
    if (!optanonConsent)
        return false;
    const otCookieValue = optanonConsent.toLowerCase();
    const consentValue = {
        performance: otCookieValue.includes(CookiesConsentKeys.PERFORMANCE),
        functional: otCookieValue.includes(CookiesConsentKeys.FUNCTIONAL),
        advertising: otCookieValue.includes(CookiesConsentKeys.ADVERTISING),
        socialMedia: otCookieValue.includes(CookiesConsentKeys.SOCIAL_MEDIA),
    };
    return Object.values(consentValue).every((item) => item);
};
export const getIsPerformanceCookiesAccepted = (optanonConsent) => {
    return optanonConsent
        ? optanonConsent.toLowerCase().includes(CookiesConsentKeys.PERFORMANCE)
        : false;
};
export const checkAccessoryHideProductCompatibility = (product) => {
    if (!product)
        return true;
    const masterVariant = getMasterVariant(product?.variants);
    return masterVariant
        ? Boolean(getAttributeByName(masterVariant?.attributes, ACCESSORY_HIDE_PRODUCT_COMPATIBILITY))
        : false;
};
export const getIsNewLabelForUnitShowed = (product) => {
    if (!product) {
        return false;
    }
    const masterVariant = getMasterVariant(product?.variants);
    const isNewLabelShowed = masterVariant
        ? Boolean(getAttributeByName(masterVariant?.attributes, SHOW_NEW_LABEL))
        : false;
    const newLabelShowedEndDate = masterVariant
        ? getAttributeByName(masterVariant?.attributes, SHOW_NEW_LABEL_UNTIL)
        : '';
    const endDate = newLabelShowedEndDate ? new Date(newLabelShowedEndDate) : undefined;
    const today = new Date();
    if (product.type === ProductType.UNIT && isNewLabelShowed) {
        if ((endDate && endDate >= today) || !endDate) {
            return true;
        }
    }
    return false;
};
